<script setup lang="ts">
import { Bold, Italic, Link2, List, ListOrdered, Quote, RemoveFormatting, Underline } from '@lucide/vue'
import { Placeholder } from '@tiptap/extensions'
import StarterKit from '@tiptap/starter-kit'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import { Button, Input } from '@zerohuman/ui'
import { onBeforeUnmount, ref, watch } from 'vue'
import { htmlToText, type MailDraft } from '@/lib/mailDraft'

/**
 * Where a mail is written (#1113): compose, reply and forward share it. Bold, italic, underline, lists, links and
 * quotes, with the usual shortcuts; what is pasted keeps those and loses the rest, since the editor knows no other
 * kind of content. The draft it writes carries the HTML and the plain-text part derived from it, so what leaves is
 * readable in any mail client.
 */
const draft = defineModel<MailDraft>({ required: true })
const props = defineProps<{ label: string; placeholder?: string; disabled?: boolean; testid?: string }>()

const editor = useEditor({
    content: draft.value.html,
    editable: !props.disabled,
    extensions: [
        StarterKit.configure({
            heading: false,
            codeBlock: false,
            horizontalRule: false,
            link: { openOnClick: false, autolink: true, defaultProtocol: 'https' },
        }),
        Placeholder.configure({ placeholder: props.placeholder ?? '' }),
    ],
    editorProps: {
        attributes: {
            class: 'prose prose-sm max-w-none min-h-32 px-3 py-2 focus:outline-none',
            role: 'textbox',
            'aria-multiline': 'true',
            'aria-label': props.label,
        },
    },
    onUpdate: ({ editor: current }) => {
        const html = current.isEmpty ? '' : current.getHTML()
        draft.value = { html, text: htmlToText(html) }
    },
})

// A draft cleared or replaced from outside (after a send) is shown; the editor's own edits are already there.
watch(
    () => draft.value.html,
    html => {
        const current = editor.value
        if (current && html !== (current.isEmpty ? '' : current.getHTML())) {
            current.commands.setContent(html, { emitUpdate: false })
        }
    },
)
watch(
    () => props.disabled,
    disabled => editor.value?.setEditable(!disabled),
)

const linkOpen = ref(false)
const linkHref = ref('')
/** Bound, not written in the template: see the vue-tsc 3.3.14 `//` note on `LINKS_EXAMPLE` in ExecutionsView.vue. */
const LINK_PLACEHOLDER = 'https://'

function startLink(): void {
    linkHref.value = editor.value?.getAttributes('link')['href'] ?? ''
    linkOpen.value = true
}

function applyLink(): void {
    const href = linkHref.value.trim()
    const chain = editor.value?.chain().focus().extendMarkRange('link')
    if (href === '') {
        chain?.unsetLink().run()
    } else {
        chain?.setLink({ href: /^[a-z][a-z0-9+.-]*:/i.test(href) ? href : `https://${href}` }).run()
    }
    linkOpen.value = false
}

function removeLink(): void {
    editor.value?.chain().focus().extendMarkRange('link').unsetLink().run()
    linkOpen.value = false
}

type Tool = { label: string; icon: typeof Bold; active: string; run: () => void }
const tools: Tool[] = [
    { label: 'Bold', icon: Bold, active: 'bold', run: () => editor.value?.chain().focus().toggleBold().run() },
    { label: 'Italic', icon: Italic, active: 'italic', run: () => editor.value?.chain().focus().toggleItalic().run() },
    {
        label: 'Underline',
        icon: Underline,
        active: 'underline',
        run: () => editor.value?.chain().focus().toggleUnderline().run(),
    },
    {
        label: 'Bulleted list',
        icon: List,
        active: 'bulletList',
        run: () => editor.value?.chain().focus().toggleBulletList().run(),
    },
    {
        label: 'Numbered list',
        icon: ListOrdered,
        active: 'orderedList',
        run: () => editor.value?.chain().focus().toggleOrderedList().run(),
    },
    {
        label: 'Quote',
        icon: Quote,
        active: 'blockquote',
        run: () => editor.value?.chain().focus().toggleBlockquote().run(),
    },
]

function clearFormatting(): void {
    editor.value?.chain().focus().unsetAllMarks().clearNodes().run()
}

onBeforeUnmount(() => editor.value?.destroy())
</script>

<template>
	<div class="border-input rounded-md border" :data-testid="testid" data-mail-editor>
		<div class="border-input flex flex-wrap items-center gap-0.5 border-b px-1 py-1" role="toolbar" :aria-label="`${label} formatting`">
			<button
				v-for="tool in tools"
				:key="tool.label"
				type="button"
				class="hover:bg-muted rounded p-1.5 disabled:opacity-50"
				:class="{ 'bg-muted text-foreground': editor?.isActive(tool.active) }"
				:aria-label="tool.label"
				:aria-pressed="editor?.isActive(tool.active) ?? false"
				:title="tool.label"
				:disabled="disabled"
				@click="tool.run"
			>
				<component :is="tool.icon" class="size-3.5" aria-hidden="true" />
			</button>
			<button
				type="button"
				class="hover:bg-muted rounded p-1.5 disabled:opacity-50"
				:class="{ 'bg-muted text-foreground': editor?.isActive('link') }"
				aria-label="Link"
				title="Link"
				:disabled="disabled"
				@click="startLink"
			>
				<Link2 class="size-3.5" aria-hidden="true" />
			</button>
			<button type="button" class="hover:bg-muted rounded p-1.5 disabled:opacity-50" aria-label="Clear formatting" title="Clear formatting" :disabled="disabled" @click="clearFormatting">
				<RemoveFormatting class="size-3.5" aria-hidden="true" />
			</button>
		</div>
		<div v-if="linkOpen" class="border-input flex flex-wrap items-center gap-2 border-b px-2 py-1.5" data-testid="mail-editor-link">
			<Input v-model="linkHref" class="h-8 min-w-48 flex-1" :placeholder="LINK_PLACEHOLDER" aria-label="Link address" @keydown.enter.prevent="applyLink" />
			<Button type="button" size="sm" @click="applyLink">Apply</Button>
			<Button type="button" size="sm" variant="outline" @click="removeLink">Remove</Button>
		</div>
		<EditorContent :editor="editor" />
	</div>
</template>

<style scoped>
:deep(.tiptap p.is-editor-empty:first-child::before) {
	content: attr(data-placeholder);
	float: left;
	height: 0;
	pointer-events: none;
	color: var(--muted-foreground);
}
</style>
